import snakeCase from 'lodash.snakecase';
import { BasketItem } from '../types/basket';
import { hasCookieConsent, isAbTestActive } from './abTests';
import { Basket, CheckoutState } from './checkoutState';
import { config } from './config';
import { Step } from './routing';

declare global {
  // eslint-disable-next-line @typescript-eslint/consistent-type-definitions
  interface Window {
    dataLayer: any;
  }
}

window.dataLayer = window.dataLayer || [];

const formatValue = (value: number) => value / 100;

export const getPriceBi = (basket: Basket, id: string) => {
  const basketItems = basket.packages.flatMap(({ items }) => items);
  const basketItem = basketItems.find(item => item.id === id);

  if (!basketItem) {
    return null;
  }

  const { costWithTaxWithDiscount, costWithTaxWithoutDiscount } = basketItem.total;
  const { sale, campaign } = basketItem.discount;

  return `${formatValue(costWithTaxWithDiscount)}|${formatValue(sale)}|${formatValue(campaign)}|${formatValue(
    costWithTaxWithoutDiscount
  )}`;
};

export const getAgeFromBirthDate = (birthDate: string) => {
  const currentYear = new Date().getFullYear();
  const birthYear = parseFloat(birthDate.split('-')[0]);
  const age = currentYear - birthYear;

  return age;
};

export const getGaEcommerceObject = (
  checkoutState: CheckoutState,
  id?: string,
  delta?: number,
  removedItem?: BasketItem,
  isRemovingLastItem?: boolean
) => {
  const { currency } = config;
  const {
    orderState: {
      basket: { total, packages, vouchers },
      shipping,
      payment
    }
  } = checkoutState;

  const selectedCarrier = shipping?.selected?.[0];
  const selectedPaymentMethod = payment?.selected?.[0];
  const basketItems =
    removedItem ? [removedItem]
    : id ? [packages.flatMap(({ items }) => items).find(i => i.id === id)!]
    : packages.flatMap(({ items }) => items);

  const items = basketItems?.map((item, index) => {
    const mainCategory = item.categories?.pop();
    return {
      item_id: item.productId.toString(),
      item_product_reference_key: item.displayData.identifier.value,
      item_name: item.name,
      price: formatValue(item.unit.costWithoutTaxWithoutVoucher),
      item_brand: item.brand.name,
      item_brand_id: item.brand.id.toString(),
      item_category: mainCategory?.name,
      item_category_id: mainCategory?.id.toString(),
      item_variant: item.variantId.toString(),
      item_variant_reference_key: item.displayData.identifier.value,
      coupon: item.promotionId ?? '',
      sale: !!item.discount.sale,
      index,
      quantity: delta?.toString() ?? item.quantity.toString(),
      ...(id && { total_quantity: removedItem ? 0 : item.quantity.toString() })
    };
  });

  return {
    currency,
    value: isRemovingLastItem ? 0 : formatValue(total.costWithTaxWithVouchersWithServiceCosts),
    payment_type: selectedPaymentMethod?.paymentOptionKey,
    shipping_tier: selectedCarrier?.carrierKey,
    coupon: vouchers ?? '',
    items
  };
};

export const pushTrackingEvent = (dataLayerProperties: Record<string, unknown>) => {
  const dataLayerPropertiesWithCorrelationId = { correlation_id: config.session.correlationId, ...dataLayerProperties };
  if (config.tracking?.sendDataLayerEvents === undefined || config.tracking.sendDataLayerEvents) {
    window.dataLayer.push(dataLayerPropertiesWithCorrelationId);
  }
  window.ayCheckoutFunctions?.dispatchEvent?.(
    new CustomEvent('track', { detail: dataLayerPropertiesWithCorrelationId })
  );
  window.parent.postMessage(
    {
      type: 'tracking',
      event: dataLayerPropertiesWithCorrelationId
    },
    '*'
  );
};

export const getCustomerType = (checkoutState: CheckoutState) => {
  if (checkoutState.customer?.flags?.isGuest) {
    return 'guest';
  } else if (checkoutState.customer?.groups?.includes('new')) {
    return 'new';
  } else {
    return 'existing';
  }
};

export const pushCustomerDataTrackingEvent = (checkoutState: CheckoutState) => {
  if (!checkoutState.customer) {
    return;
  }

  pushTrackingEvent({
    event: 'customer_data',
    customer_id: checkoutState.customer.data.id,
    unique_customer_id: checkoutState.customer?.data?.customData?.customer_unique_id,
    customer_type: getCustomerType(checkoutState),
    age: checkoutState.customer.data?.birthDate ? getAgeFromBirthDate(checkoutState.customer.data.birthDate) : null,
    gender: checkoutState.customer.data.gender,
    sessions_id_co: 'sessionIdCheckout',
    eh: checkoutState.customer.data.trackingHash,
    ...(config.tracking.sendPlaintextEmail && { email: checkoutState.customer.data.email }),
    data: {
      customerAddedNumber: !!checkoutState.customer.data.phone
    },
    logged_in: 'email'
  });
};

export const pushInitialTrackingEvents = (initialCheckoutState: CheckoutState, step: Step) => {
  pushTrackingEvent({
    event: 'shop_init',
    shop_id: config.appId,
    shop_version: RELEASE_ID,
    shop_gender: initialCheckoutState.customer?.data.gender,
    locale: config.locale,
    shop_currency: config.currency,
    landing_page: window.location.href.replace('#', ''),
    parameter: window.location.search,
    referrer: document?.referrer,
    deeplink: '<deeplink>',
    checkout_version: 'v3',
    origin: config.origin,
    ecommerce: getGaEcommerceObject(initialCheckoutState)
  });

  config.abTests.forEach(abTest => {
    if (abTest.experimentId === 'ratepayHidden') {
      pushTrackingEvent({
        event: 'abtest',
        content_name: `${window.location.pathname}/${step}`,
        page_type: `checkout:${step}`,
        page_type_id: '',
        action: `ab_${abTest.experimentId}`,
        label: isAbTestActive('ratepayHidden') ? 'rh-1' : 'rh-c'
      });
    }

    if (
      abTest.experimentId === 'creditCardTest' &&
      hasCookieConsent() &&
      config.shop === 'ay' &&
      !initialCheckoutState.orderOptions?.payment?.recurring?.length &&
      [139, 688, 431, 566, 715, 554, 658].includes(config.appId)
    ) {
      pushTrackingEvent({
        event: 'abtest',
        content_name: `${window.location.pathname}/${step}`,
        page_type: `checkout:${step}`,
        page_type_id: '',
        action: 'ab_creditCardTest',
        label: isAbTestActive('creditCardTest') ? 'cc-1' : 'cc-c'
      });
    }
  });

  pushCustomerDataTrackingEvent(initialCheckoutState);
};

export const pushErrorTrackingEventFromI18nKey = (i18nKey?: string, providedAction?: string, value?: string) => {
  if (!i18nKey) {
    return;
  }
  const parts = i18nKey.split('.');
  const action = providedAction ?? snakeCase(parts[1] === 'notifications' ? parts[0] : parts[1]);
  const label = `${config.country}|${snakeCase(parts[parts.length - 1])}`;
  pushTrackingEvent({ event: 'error', action, label, value });
};

export const pushErrorTrackingEventFromErrorObject = (errorObj: any, action: string, value?: string) => {
  const label = `${config.country}|${
    !errorObj ? 'unknown' : snakeCase([errorObj.name, errorObj.message].filter(Boolean).join(' '))
  }`;
  pushTrackingEvent({ event: 'error', action, label, value });
};

export const isItemQuantityEqual = (checkoutState: CheckoutState, newState: CheckoutState, itemId: string) =>
  newState.orderState.basket.packages.flatMap(({ items }) => items).find(i => i.id === itemId)?.quantity ===
  checkoutState!.orderState.basket.packages.flatMap(({ items }) => items).find(i => i.id === itemId)?.quantity;
